import React, { useMemo, useState } from 'react';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import { attachmentService } from '../services/attachmentService';
import { ATTACHMENT_CATEGORY_IDS, ATTACHMENT_CATEGORY_LABELS } from '../constants/attachmentCategories';

/**
 * Small toggle chips for engineering / financial / shop on an attachment row.
 */
export default function AttachmentCategoryChips({
  attachmentId,
  categories,
  disabled = false,
  onUpdated,
  onError,
}) {
  const active = useMemo(() => {
    const list = Array.isArray(categories) ? categories : [];
    return new Set(list.filter((c) => ATTACHMENT_CATEGORY_IDS.includes(c)));
  }, [categories]);
  const [pendingKey, setPendingKey] = useState(null);

  const handleToggle = async (key) => {
    if (disabled || pendingKey) return;
    const next = new Set(active);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    const nextArr = ATTACHMENT_CATEGORY_IDS.filter((id) => next.has(id));
    setPendingKey(key);
    try {
      const res = await attachmentService.updateCategories(attachmentId, nextArr);
      const row = res?.data;
      if (row) onUpdated?.(row);
    } catch (e) {
      onError?.(e.response?.data?.message || 'Failed to update categories');
    } finally {
      setPendingKey(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', maxWidth: 280 }}>
      {ATTACHMENT_CATEGORY_IDS.map((key) => {
        const selected = active.has(key);
        return (
          <Chip
            key={key}
            label={ATTACHMENT_CATEGORY_LABELS[key] || key}
            size="small"
            variant={selected ? 'filled' : 'outlined'}
            color={selected ? 'primary' : 'default'}
            disabled={disabled || pendingKey != null}
            onClick={() => handleToggle(key)}
            sx={{
              height: 22,
              fontSize: '0.7rem',
              '& .MuiChip-label': { px: 0.75 },
              cursor: disabled || pendingKey ? 'default' : 'pointer',
              opacity: pendingKey && pendingKey !== key ? 0.65 : 1,
            }}
          />
        );
      })}
    </Box>
  );
}
